import { DatePipe } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { OriginEvent } from "@core/models";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, map, Observable, of, switchMap } from "rxjs";
import { AppConfigService } from "@core/services/config.service";

@Injectable()
export class EventService {

  private readonly http = inject(HttpClient);
  private readonly datePipe = inject(DatePipe);
  private readonly appConfigService = inject(AppConfigService);
  private readonly translateService = inject(TranslateService);

  /**
   * Random number between min and max
   * @param min
   * @param max
   * @returns number integer
   */
  randomInteger(min = 1, max = 5) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  getEvents(sellerId: string, startMin?: string, rootId?: string, top?: number, eventIds?: string[], underShopId?: string): Observable<OriginEvent[]> {
    const startDate = startMin ? new Date(startMin) : new Date(); //if startMin not provided, use current date
    startDate.setHours(0, 0, 0, 0);
    const daysDifference = startMin ? 30 : 14; //30 days if startMin is provided, 14 days if not
    //calculate end date based on the start date + days difference
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + daysDifference);
    endDate.setHours(23, 59, 59, 999);

    let queryParams = `sellerId=${sellerId}`;
    if (startDate) queryParams = `${queryParams}&startMin=${startDate.toISOString()}`; //UTC date format
    if (endDate) queryParams = `${queryParams}&endMax=${endDate.toISOString()}`; //UTC date format
    if (rootId) queryParams = `${queryParams}&rootId=${rootId}`;
    if (top) queryParams = `${queryParams}&top=${top}`;
    if (eventIds) queryParams = `${queryParams}&eventIds[]=${eventIds.join(',')}`;
    if (underShopId) queryParams = `${queryParams}&underShopId=${underShopId}`;

    // Add Current Language
    queryParams = `${queryParams}&locale=${this.translateService.currentLang}`;

    // Add Sort
    queryParams = `${queryParams}&sortBy=start&sortOrder=ASC`;

    return this.http.get<any>(`${this.appConfigService.host()}/events/public/listings?${queryParams}`).pipe(
      map(response => response.filter((event: OriginEvent) =>  !!event.meta?.guestup_kiosk && ["onSale", "soldOut"].includes(event.saleStatus)))
    );
  }

  resolverEvent(): Observable<any> {
    if (!this.appConfigService.currentConfig()) return of(null);
    return this.getEvents(this.appConfigService.currentConfig().sellerId, undefined, undefined, undefined).pipe(
      switchMap((events: OriginEvent[]) => {
        //Group events by rootId (because API send same events with multiple sessions)
        events = this.groupEventsByRootId(events);
        if (events.length === 1) {
          return forkJoin({
            highlightedEvent: this.getEventById(events[0]._id),
          }).pipe(
            switchMap(data => of({
              highlightedEvent: {...data.highlightedEvent, rootId: events[0].rootId},
              mainEvent: {...data.highlightedEvent, rootId: events[0].rootId},
              events
            }))
          )
        } else if (events.length > 1) {
          let splashEvents = events.filter((event: OriginEvent) => (event.meta?.guestup_kiosk === "splash" || event.meta?.guestup_kiosk === "splash-home"));
          let homeEvents = events.filter((event: OriginEvent) => (event.meta?.guestup_kiosk === "home" || event.meta?.guestup_kiosk === "splash-home"));
          if (!homeEvents.length) homeEvents = splashEvents;

          const todayDate = new Date().toISOString().split("T")[0];
          let mainEvent = homeEvents.find(event => {
            const eventDate = new Date(event.start).toISOString().split("T")[0];
            return eventDate === todayDate;
          });
          mainEvent = mainEvent ?? homeEvents?.[0];
          //Remove main event from events list, and get only first 4 events
          const first4Events = events.filter((event: OriginEvent) => event._id !== mainEvent?._id).slice(0, 4);
          return forkJoin({
            highlightedEvent: this.getEventById(splashEvents?.[0]?._id),
            mainEvent: this.getEventById(mainEvent?._id),
            events: forkJoin(first4Events.map(event => this.getEventById(event._id)))
          }).pipe(
            switchMap(data => of({
              highlightedEvent: {...data.highlightedEvent, rootId: splashEvents[0].rootId},
              mainEvent: {...data.mainEvent, rootId: mainEvent.rootId},
              events: data.events.map((event: OriginEvent, index: number) => ({...event, rootId: first4Events[index].rootId}))
            }))
          )
        } else {
          return of({});
        }
      })
    )
  }

  getEventById(id: string) {
    // Add Current Language
    const queryParams = `locale=${this.translateService.currentLang}`;

    return this.http.get<any>(`${this.appConfigService.host()}/events/info/${id}?${queryParams}`);
  }

  getPrivateEventById(id: string) {
    // Add Current Language
    const queryParams = `locale=${this.translateService.currentLang}`;

    return this.http.get<any>(`${this.appConfigService.host()}/events/${id}?${queryParams}`);
  }

  groupEventsByRootId(events: OriginEvent[]): OriginEvent[] {
    const uniqueEventsMap = new Map();
    for (const event of events) {
      if (!uniqueEventsMap.has(event.rootId)) {
        uniqueEventsMap.set(event.rootId, event);
      }
    }
    return Array.from(uniqueEventsMap.values());
  }
}
